(function () {
  function capacityFromDescription(description) {
    const match = (description || '').match(/【Capacity】\s*[:：]?\s*([0-9.]+)\s*ML/i);
    return match ? Number(match[1]) : null;
  }

  function capacityBand(capacity) {
    if (!capacity) return '';
    if (capacity <= 180) return 'small';
    if (capacity <= 280) return 'medium';
    return 'large';
  }

  function productPrice(product) {
    const availablePrices = (product.variants || [])
      .filter((variant) => variant.available)
      .map((variant) => Number(variant.price))
      .filter(Number.isFinite);
    return availablePrices.length ? Math.min(...availablePrices) : null;
  }

  function matchesUse(product, use) {
    if (!use) return true;
    const types = {
      brew: ['Teapots', 'Premium'],
      drink: ['TeaCup'],
      display: ['Tea Pets'],
      sculptural: ['Carving'],
    };
    return (types[use] || []).includes(product.product_type);
  }

  function filterByBudgetBand(products, budget) {
    if (!budget) return products;
    const pricedProducts = products
      .map((product) => ({ product, price: productPrice(product) }))
      .filter((item) => item.price !== null)
      .sort((a, b) => a.price - b.price);
    if (!pricedProducts.length) return [];
    const lowerBoundary = pricedProducts[Math.floor((pricedProducts.length - 1) / 3)].price;
    const upperBoundary = pricedProducts[Math.floor(((pricedProducts.length - 1) * 2) / 3)].price;
    return pricedProducts
      .filter((item) => {
        if (budget === 'lower') return item.price <= lowerBoundary;
        if (budget === 'middle') return item.price > lowerBoundary && item.price <= upperBoundary;
        return item.price > upperBoundary;
      })
      .map((item) => item.product);
  }

  function pushEvent(event, details) {
    const eventData = {
      finder_version: '2',
      page_path: window.location.pathname,
      ...details,
    };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...eventData });

    const publish = window.Shopify?.analytics?.publish;
    if (typeof publish !== 'function') return;

    try {
      const publication = publish.call(
        window.Shopify.analytics,
        event.replace(/^claypurple_/, 'claypurple:'),
        eventData
      );
      if (publication && typeof publication.catch === 'function') {
        publication.catch(() => {});
      }
    } catch (error) {
      // Analytics must never interrupt the finder experience.
    }
  }

  function selectedLabel(form, name) {
    const field = form.elements[name];
    return field?.value ? field.selectedOptions[0]?.textContent.trim() : '';
  }

  function matchReasons(form, people, requestedCapacity, budget, use, shape, fallback) {
    const reasons = [];
    const explicitCapacity = form.elements.capacity?.value;
    if (explicitCapacity) {
      reasons.push(selectedLabel(form, 'capacity'));
    } else if (people && requestedCapacity) {
      reasons.push(`${selectedLabel(form, 'people')} starting range`);
    }
    if (budget) reasons.push(selectedLabel(form, 'budget'));
    if (use) reasons.push(selectedLabel(form, 'use'));
    if (shape) reasons.push(`title contains “${form.elements.shape.selectedOptions[0].textContent.trim()}”`);
    return reasons.length ? reasons.join(' · ') : fallback;
  }

  function initFinder(section) {
    const form = section.querySelector('[data-pot-finder-form]');
    const results = section.querySelector('[data-pot-finder-results]');
    const status = section.querySelector('[data-pot-finder-status]');
    const grid = section.querySelector('[data-pot-finder-grid]');
    const button = section.querySelector('[data-pot-finder-submit]');
    const fallback = section.querySelector('[data-pot-finder-fallback]');
    if (!form || !results || !status || !grid || !button || !fallback) return;

    let catalogPromise;

    function catalog() {
      if (!catalogPromise) {
        catalogPromise = fetch(section.dataset.productsUrl, {
          headers: { Accept: 'application/json' },
        }).then((response) => {
          if (!response.ok) throw new Error('Catalog request failed');
          return response.json();
        });
      }
      return catalogPromise;
    }

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      button.disabled = true;
      button.textContent = section.dataset.loadingLabel;
      results.hidden = false;
      status.textContent = section.dataset.readingLabel;
      fallback.hidden = true;
      grid.replaceChildren();

      try {
        const formData = new FormData(form);
        const people = formData.get('people');
        const requestedCapacity = formData.get('capacity') || ({ one: 'small', two: 'medium', group: 'large' }[people] || '');
        const budget = formData.get('budget');
        const use = formData.get('use');
        const shape = String(formData.get('shape') || '').toLowerCase();
        pushEvent('claypurple_finder_submit', {
          people: people || 'any',
          capacity: requestedCapacity || 'any',
          budget: budget || 'any',
          intended_use: use || 'any',
          shape: shape || 'any',
        });

        const payload = await catalog();

        const baseMatches = (payload.products || []).filter((product) => {
          if (!(product.variants || []).some((variant) => variant.available)) return false;
          const capacity = capacityFromDescription(product.body_html);
          if (requestedCapacity && capacityBand(capacity) !== requestedCapacity) return false;
          if (!matchesUse(product, use)) return false;
          return true;
        });
        const matches = filterByBudgetBand(baseMatches, budget).filter((product) => {
          return !shape || product.title.toLowerCase().includes(shape);
        });
        const currency = section.dataset.currency || 'USD';
        const locale = section.dataset.locale || 'en';
        const currencyFormatter = new Intl.NumberFormat(locale, { style: 'currency', currency });

        const reason = matchReasons(form, people, requestedCapacity, budget, use, shape, section.dataset.defaultReason);
        matches.slice(0, 5).forEach((product, index) => {
          const item = document.createElement('li');
          item.className = 'pot-finder__card';
          const link = document.createElement('a');
          link.href = `${window.Shopify?.routes?.root || '/'}products/${product.handle}`;
          const imageSource = product.images?.[0]?.src;
          if (imageSource) {
            const image = document.createElement('img');
            image.src = imageSource;
            image.alt = product.title;
            image.loading = 'lazy';
            link.appendChild(image);
          }
          const title = document.createElement('h3');
          title.textContent = product.title;
          link.appendChild(title);
          const meta = document.createElement('p');
          meta.className = 'pot-finder__card-meta';
          const capacity = capacityFromDescription(product.body_html);
          const price = productPrice(product);
          const facts = [product.product_type];
          if (capacity) facts.push(`${capacity} ML listed capacity`);
          if (price !== null) facts.push(`${currencyFormatter.format(price)} current catalog price`);
          meta.textContent = facts.filter(Boolean).join(' · ');
          link.appendChild(meta);
          const reasonText = document.createElement('p');
          reasonText.className = 'pot-finder__card-reason';
          reasonText.textContent = `${section.dataset.matchLabel}: ${reason}`;
          link.appendChild(reasonText);
          const verifyText = document.createElement('p');
          verifyText.className = 'pot-finder__card-verify';
          verifyText.textContent = section.dataset.verifyLabel;
          link.appendChild(verifyText);
          link.addEventListener('click', () => {
            pushEvent('claypurple_finder_result_click', {
              product_handle: product.handle,
              result_position: index + 1,
            });
          });
          item.appendChild(link);
          grid.appendChild(item);
        });

        if (matches.length) {
          status.textContent = section.dataset.resultsLabel.replace('{count}', matches.length);
        } else {
          status.textContent = section.dataset.emptyLabel;
          fallback.hidden = false;
        }
        pushEvent('claypurple_finder_results', { result_count: matches.length });
      } catch (error) {
        status.textContent = section.dataset.errorLabel;
        fallback.hidden = false;
        catalogPromise = null;
      } finally {
        button.disabled = false;
        button.textContent = section.dataset.submitLabel;
      }
    });
  }

  function initAllFinders(root) {
    (root || document).querySelectorAll('[data-pot-finder]').forEach(initFinder);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAllFinders(document));
  } else {
    initAllFinders(document);
  }

  document.addEventListener('shopify:section:load', (event) => initAllFinders(event.target));
})();
